import Vue from 'vue';
import {
  GLOBAL_ERROR,
  INITIAL_QUOTE,
  NEW_USED_LIST,
  DEFAULT_CUSTOMER_OPTIONS,
  GQL_ERROR_CODES
} from '@/utils/constants';
import { getDealersPrice as apiDealersPrice } from '../../api/dealers';
import {
  getQuoteForUser as apiGetQuote,
  createQuote as apiCreateQuote,
  acceptQuote as apiAcceptQuote,
  getColoursAndExtras as getColoursAndExtrasApi,
  getQuotePDF as getQuotePDFApi
} from '../../api/quotes';
import _get from 'lodash/get';
import { getUser as getUserApi } from '../../api/user';
import UserProfileStepper from '@/widgets/user-profile-stepper';

export async function getQuote({ commit, dispatch, rootState }, id) {
  return new Promise(async (resolve, reject) => {
    if (!id) {
      return reject();
    }

    try {
      commit('setState', ['loadingQuote', true]);
      const quote = (await apiGetQuote(id)) || { ...INITIAL_QUOTE };
      commit('setQuote', quote);
      commit('setState', ['displayQuoteAcceptance', false]);
      commit('setState', ['loadingQuote', false]);
      return resolve();
    } catch (err) {
      commit('setState', ['loadingQuote', false]);
      if (err.message === GQL_ERROR_CODES.notFound) {
        return reject(err.message);
      } else {
        Vue.prototype.$euiNotifications.emit({
          notification: GLOBAL_ERROR,
          action: () => dispatch('getQuote')
        });
      }
      return reject();
    }
  });
}

export async function acceptQuote({ commit, dispatch, state }) {
  return new Promise(async (resolve, reject) => {
    try {
      commit('ui/setDisableNavigation', true, { root: true });
      commit('setState', ['acceptingQuote', true]);
      await apiAcceptQuote({
        quoteId: state.quote.quoteId,
        privacyDocumentOpenedDate: state.privacyDocumentOpenedDate,
        privacyDocumentAcceptedDate: state.privacyDocumentAcceptedDate,
        termsDocumentOpenedDate: state.termsDocumentOpenedDate,
        termsDocumentAcceptedDate: state.termsDocumentAcceptedDate,
        leaseDocumentOpenedDate: state.leaseDocumentOpenedDate,
        leaseDocumentAcceptedDate: state.leaseDocumentAcceptedDate
      });
      commit('ui/setDisableNavigation', false, { root: true });
      commit('setState', ['acceptingQuote', false]);
      return resolve();
    } catch (err) {
      commit('ui/setDisableNavigation', false, { root: true });
      commit('setState', ['acceptingQuote', false]);
      Vue.prototype.$euiNotifications.emit({
        notification: GLOBAL_ERROR,
        action: () => dispatch('acceptQuote')
      });
      return reject();
    }
  });
}

export async function getDealersPrice({ commit, dispatch, state }) {
  return new Promise(async (resolve, reject) => {
    try {
      const dealersPrice = await apiDealersPrice(state.quote.variant);
      commit('setDealersPrice', dealersPrice);
      return resolve();
    } catch (err) {
      Vue.prototype.$euiNotifications.emit({
        notification: GLOBAL_ERROR,
        action: () => dispatch('getDealersPrice')
      });
      return reject();
    }
  });
}

export async function createQuote(
  { commit, dispatch, rootState, rootGetters },
  { componentContext, updateApplicationPaths }
) {
  return new Promise(async (resolve, reject) => {
    try {
      commit('ui/setDisableNavigation', true, { root: true });
      commit('setState', ['savingQuote', true]);

      if (!rootGetters['user/canUserCreateQuote']) {
        Vue.prototype.SAIDialog({
          header: 'Profile Incomplete',
          body: 'You must complete your profile so we can verify your details before you can accept the quote.',
          okText: 'Complete Profile',
          onOk: () => {
            Vue.prototype.SAIFullScreenDialog({
              parent: componentContext,
              noRouteDismiss: true,
              displayHideAction: true,
              child: UserProfileStepper
            });
          }
        });
        commit('ui/setDisableNavigation', false, { root: true });
        commit('setState', ['savingQuote', false]);
        return reject();
      }

      // A quote will not be created if the user has no organisation ID
      const user = await getUserApi();
      if (!user.hasOrganisation) {
        Vue.prototype.SAIDialog({
          body: `Your employer is not currently set up in our system.
          Our team will contact your employer and then be in touch with you shortly.`,
          okText: 'Ok'
        });
        commit('ui/setDisableNavigation', false, { root: true });
        commit('setState', ['savingQuote', false]);
        return reject();
      }

      let customerOptions = _get(
        rootState,
        'application.application.metaData.customerOptions',
        {}
      );
      customerOptions = { ...customerOptions };
      const adhoc = customerOptions.adhocEquipment || [];

      let quotePayLoad = {
        variant: { id: customerOptions.variant },
        leaseTerm: customerOptions.leaseTerm,
        kmsPerYear: customerOptions.kmsPerYear,
        customer: { annualSalary: customerOptions.annualSalary },
        exteriorColour: customerOptions.exteriorColour,
        interiorColour: customerOptions.interiorColour,
        optionalEquipment: customerOptions.optionalEquipment,
        includedEquipment: customerOptions.includedEquipment,
        odometer: customerOptions.odometer,
        price: customerOptions.carPrice,
        registrationNumber: customerOptions.registrationNumber,
        isNew: customerOptions.vehicleCondition === NEW_USED_LIST.new.value,
        adhocEquipment: adhoc.map((name) => ({ name })),
        carIsFrom: customerOptions.carIsFrom
      };
      const plateYear = _get(rootState, 'carSearch.selected.year');
      if (plateYear) {
        quotePayLoad.year = plateYear;
      }

      const createdQuote = await apiCreateQuote(quotePayLoad);
      commit('setQuote', { ...INITIAL_QUOTE, ...createdQuote });

      // Reset the car search and application customer options so the user
      // can create a fresh new quote
      commit('carSearch/resetSelected', undefined, { root: true });
      let updateApplication = { ...rootState.application.application };
      updateApplication.metaData.customerOptions = {
        ...DEFAULT_CUSTOMER_OPTIONS
      };
      commit('application/setApplication', updateApplication, { root: true });

      await dispatch(
        'application/updateApplication',
        { updateApplicationPaths, disableSaleForceUpdate: true },
        {
          root: true
        }
      );

      commit('ui/setDisableNavigation', false, { root: true });
      commit('setState', ['savingQuote', false]);
      return resolve();
    } catch (err) {
      commit('ui/setDisableNavigation', false, { root: true });
      commit('setState', ['savingQuote', false]);
      Vue.prototype.$euiNotifications.emit({
        notification: GLOBAL_ERROR,
        action: () => dispatch('createQuote')
      });
      return reject();
    }
  });
}

export async function getColoursAndExtras({ commit, dispatch, rootState }) {
  return new Promise(async (resolve, reject) => {
    try {
      commit('setState', ['loadingColoursAndExtras', true]);
      let variantId = _get(
        rootState,
        'application.application.metaData.customerOptions.variant'
      );
      if (variantId) {
        const coloursAndExtras = await getColoursAndExtrasApi(variantId);
        commit('setColoursAndExtras', coloursAndExtras);
      }
      commit('setState', ['loadingColoursAndExtras', false]);
    } catch (err) {
      commit('setState', ['loadingColoursAndExtras', false]);
      Vue.prototype.$euiNotifications.emit({
        notification: GLOBAL_ERROR,
        action: () => dispatch('getColoursAndExtras')
      });
      return reject();
    }
  });
}

export async function getQuotePDF({ commit, dispatch, state }) {
  return new Promise(async (resolve, reject) => {
    if (!state.quote.quoteId) {
      return reject(new Error('No quote exists'));
    }
    try {
      commit('setState', ['loadingQuotePDF', true]);
      const quotePDF = await getQuotePDFApi(state.quote.quoteId);

      commit('setQuotePDFHref', quotePDF.signedUrl);
      commit('setState', ['loadingQuotePDF', false]);
      return resolve();
    } catch (err) {
      commit('setState', ['loadingQuotePDF', false]);
      Vue.prototype.$euiNotifications.emit({
        notification: GLOBAL_ERROR,
        action: () => dispatch('getQuotePDF')
      });
      return reject();
    }
  });
}
